import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatAmount } from "./numberUtils";
import moment from "moment";

export const exportToExcel = (rows, sheetName = "Sheet1", filePrefix = "Export") => {
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(rows);
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  const timestamp = new Date().toISOString().replace(/[:T]/g, "-").split(".")[0];
  XLSX.writeFile(workbook, `${filePrefix}-${timestamp}.xlsx`);
};

export const exportToPDF = (rows, headers, orientation = "landscape", filePrefix = "Export") => {
  const doc = new jsPDF({ orientation });
  const body = rows.map((r) => headers.map((h) => r[h]));
  autoTable(doc, { 
    head: [headers], 
    body,
    headStyles: {
      fillColor: '#1E3A8A',
      textColor: '#FFFFFF',
      fontStyle: 'bold'
    }
  });
  const timestamp = new Date().toISOString().replace(/[:T]/g, "-").split(".")[0];
  doc.save(`${filePrefix}-${timestamp}.pdf`);
};

export const mapExpensesForExport = (expenseList = []) => {
  return expenseList.map((item) => ({
    Description: item?.description || "",
    Project: item?.project?.title || item?.project_id || "",
    Amount: formatAmount(item?.input_amount ?? item?.amount ?? ""),
    Tax: item?.tax_amount != null ? formatAmount(item?.tax_amount) : "-",
    "Total Amount": formatAmount(item?.amount ?? item?.input_amount ?? ""),
    Period: item?.invoice_date ? item?.invoice_date?.slice(0, 10) : "",
    Vendor: item?.vendor?.title || "",
    "Created By": item?.ExpenseCreator?.role ? item.ExpenseCreator.role.charAt(0).toUpperCase() + item.ExpenseCreator.role.slice(1) : "-",
  }));
};

export const exportWorkerHoursToPDF = (reportData, startDate, endDate) => {
  const doc = new jsPDF({ orientation: 'landscape' });
  
  // Add title
  doc.setFontSize(18);
  doc.setTextColor(30, 58, 138); // #1E3A8A
  doc.text('Worker Hours Report', 14, 20);
  
  // Add date range
  doc.setFontSize(12);
  doc.setTextColor(75, 85, 99); // #4B5563
  doc.text(`Period: ${moment(startDate).format('MMM DD, YYYY')} - ${moment(endDate).format('MMM DD, YYYY')}`, 14, 28);
  
  // Prepare table data
  const headers = [
    'Worker Name',
    'Date',
    'Check In',
    'Check Out',
    'Working Hours',
    'Break Time',
    'Travel Time'
  ];
  
  // Helpers to safely format values without producing "Invalid date"
  const formatDateSafe = (value) => {
    if (!value) return 'N/A';
    // If it's a date range string like "2025-10-01 - 2025-10-18", return as-is
    if (typeof value === 'string' && value.includes(' - ')) return value;
    const m = moment(value, ['YYYY-MM-DD', 'MMM DD, YYYY', moment.ISO_8601], true);
    return m.isValid() ? m.format('MMM DD, YYYY') : (typeof value === 'string' ? value : 'N/A');
  };

  const formatTimeSafe = (value, timeZone) => {
    if (!value) return 'N/A';
    if (typeof value === 'string' && (value.includes('T') || value.endsWith('Z'))) {
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) {
        try {
          return new Intl.DateTimeFormat('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
            timeZone: timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          }).format(parsed);
        } catch (_) {
          return value;
        }
      }
      return value;
    }
    const m = moment(value, ['HH:mm', 'hh:mm A', moment.ISO_8601], true);
    return m.isValid() ? m.format('hh:mm A') : (typeof value === 'string' ? value : 'N/A');
  };

  const body = reportData.map(row => [
    row.workerName,
    formatDateSafe(row.date),
    formatTimeSafe(row.checkInTime, row.localTimezone),
    formatTimeSafe(row.checkOutTime, row.localTimezone),
    row.totalWorkingTime || '00:00',
    row.totalBreakTime || '00:00',
    row.totalTravelTime || '00:00'
  ]);
  
  // Calculate totals by worker
  const workerTotals = {};
  const parseDurationToSeconds = (timeStr) => {
    if (!timeStr || timeStr === "N/A") return 0;
    const parts = String(timeStr)
      .trim()
      .split(":")
      .map((v) => Number.parseInt(v, 10));
    if (parts.some((n) => Number.isNaN(n))) return 0;

    // Supports HH:MM:SS and HH:MM
    if (parts.length >= 3) {
      return (parts[0] * 3600) + (parts[1] * 60) + parts[2];
    }
    if (parts.length === 2) {
      return (parts[0] * 3600) + (parts[1] * 60);
    }
    if (parts.length === 1) {
      return parts[0] * 3600;
    }
    return 0;
  };

  reportData.forEach(row => {
    if (!workerTotals[row.workerName]) {
      workerTotals[row.workerName] = {
        workingTime: 0,
        breakTime: 0,
        travelTime: 0,
        days: 0
      };
    }

    workerTotals[row.workerName].workingTime += parseDurationToSeconds(row.totalWorkingTime);
    workerTotals[row.workerName].breakTime += parseDurationToSeconds(row.totalBreakTime);
    workerTotals[row.workerName].travelTime += parseDurationToSeconds(row.totalTravelTime);
    const isPlaceholderDay =
      row?.checkInTime === "-" &&
      row?.checkOutTime === "-" &&
      row?.totalWorkingTime === "-" &&
      row?.totalBreakTime === "-" &&
      row?.totalTravelTime === "-";

    if (!isPlaceholderDay) {
      workerTotals[row.workerName].days += 1;
    }
  });

  // Format seconds back to HH:MM:SS for exact totals.
  const formatDurationHHMMSS = (seconds) => {
    const totalSeconds = Math.max(0, Math.round(Number(seconds) || 0));
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    return `${hours.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };
  
  // Add main table
  autoTable(doc, {
    head: [headers],
    body: body,
    startY: 35,
    margin: { bottom: 20 }, // Add bottom margin to prevent overlap
    headStyles: {
      fillColor: [30, 58, 138], // #1E3A8A
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 10
    },
    bodyStyles: {
      fontSize: 9
    },
    alternateRowStyles: {
      fillColor: [249, 250, 251] // Light gray
    },
    columnStyles: {
      0: { cellWidth: 40 }, // Worker Name
      1: { cellWidth: 35 }, // Date
      2: { cellWidth: 30 }, // Check In
      3: { cellWidth: 30 }, // Check Out
      4: { cellWidth: 35 }, // Working Hours
      5: { cellWidth: 30 }, // Break Time
      6: { cellWidth: 30 }  // Travel Time
    }
  });
  
  // Add summary section
  const finalY = doc.lastAutoTable.finalY || 35;
  doc.setFontSize(14);
  doc.setTextColor(30, 58, 138);
  doc.text('Summary by Worker', 14, finalY + 15);
  
  const summaryHeaders = [
    'Worker Name',
    'Days Worked',
    'Total Working Hours',
    'Total Break Time',
    'Total Travel Time'
  ];
  
  const summaryBody = Object.keys(workerTotals).map(workerName => [
    workerName,
    workerTotals[workerName].days.toString(),
    formatDurationHHMMSS(workerTotals[workerName].workingTime),
    formatDurationHHMMSS(workerTotals[workerName].breakTime),
    formatDurationHHMMSS(workerTotals[workerName].travelTime)
  ]);
  
  autoTable(doc, {
    head: [summaryHeaders],
    body: summaryBody,
    startY: finalY + 20,
    margin: { bottom: 20 }, // Add bottom margin to prevent overlap
    headStyles: {
      fillColor: [249, 115, 22], // #F97316
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 10
    },
    bodyStyles: {
      fontSize: 9,
      fontStyle: 'bold'
    },
    columnStyles: {
      0: { cellWidth: 60 }, // Worker Name
      1: { cellWidth: 35 }, // Days Worked
      2: { cellWidth: 50 }, // Total Working Hours
      3: { cellWidth: 45 }, // Total Break Time
      4: { cellWidth: 45 }  // Total Travel Time
    }
  });
  
  // Add footer with proper spacing
  const pageCount = doc.internal.getNumberOfPages();
  doc.setFontSize(8);
  doc.setTextColor(128, 128, 128);
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    // Position footer at the very bottom with more margin
    doc.text(
      `Generated on ${moment().format('MMM DD, YYYY hh:mm A')} - Page ${i} of ${pageCount}`,
      14,
      doc.internal.pageSize.height - 5
    );
  }
  
  // Save the PDF
  const timestamp = new Date().toISOString().replace(/[:T]/g, "-").split(".")[0];
  doc.save(`Worker-Hours-Report-${timestamp}.pdf`);
};
